import { useCallback, useRef, useState } from "react";
import { Camera, ImageIcon, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  usePropertyImages,
  useUploadPropertyImages,
  type UploadProgressTick,
} from "@/features/property/api";
import { ImageUploaderCard } from "@/features/property/components/image-uploader-card";
import { PropertyImageGrid } from "@/features/property/components/property-image-grid";
import type { PropertyImage } from "@/types/property";

interface ImageUploaderProps {
  propertyId: number;
  /** Optional initial images; defaults to the live TanStack-Query list. */
  initialImages?: PropertyImage[] | undefined;
  onUploadComplete?: ((newImages: PropertyImage[]) => void) | undefined;
  maxFiles?: number | undefined;
}

const DEFAULT_MAX_FILES = 20;
const ACCEPT_MIME = "image/jpeg,image/png,image/webp";
const ACCEPT_EXT = new Set(["image/jpeg", "image/png", "image/webp"]);

interface QueueEntry {
  /** Unique key — file identity is unreliable across remounts. */
  key: string;
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  percent: number;
  error?: unknown;
}

function makeKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function ImageUploader({
  propertyId,
  onUploadComplete,
  maxFiles = DEFAULT_MAX_FILES,
}: ImageUploaderProps) {
  const imagesQuery = usePropertyImages(propertyId);
  const uploadMutation = useUploadPropertyImages(propertyId);

  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const acceptFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      // Filter out non-allowed types up-front so the user sees the
      // problem before hitting upload. (Backend would also reject.)
      const allowed = incoming.filter((f) => ACCEPT_EXT.has(f.type));
      setQueue((prev) => {
        const room = Math.max(0, maxFiles - prev.length);
        const slice = allowed.slice(0, room);
        return [
          ...prev,
          ...slice.map<QueueEntry>((file) => ({
            key: makeKey(file),
            file,
            status: "pending",
            percent: 0,
          })),
        ];
      });
    },
    [maxFiles],
  );

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list) return;
    acceptFiles(Array.from(list));
    // Reset so picking the same file twice still fires onChange.
    e.target.value = "";
  };

  const onDropFiles = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!e.dataTransfer.files) return;
    acceptFiles(Array.from(e.dataTransfer.files));
  };

  const removeFromQueue = (key: string) => {
    setQueue((prev) => prev.filter((entry) => entry.key !== key));
  };

  const clearQueue = () => setQueue([]);

  const handleProgress = useCallback((tick: UploadProgressTick) => {
    setQueue((prev) =>
      prev.map((entry) =>
        entry.file === tick.file
          ? {
              ...entry,
              status: tick.status,
              percent: tick.percent,
              error: tick.error,
            }
          : entry,
      ),
    );
  }, []);

  const startUpload = async () => {
    const pending = queue.filter((e) => e.status === "pending" || e.status === "error");
    if (pending.length === 0) return;
    // Re-arm error entries back to pending so the percent bar resets.
    setQueue((prev) =>
      prev.map((entry) =>
        entry.status === "error"
          ? { ...entry, status: "pending", percent: 0, error: undefined }
          : entry,
      ),
    );
    try {
      const uploaded = await uploadMutation.mutateAsync({
        files: pending.map((p) => p.file),
        onPerFileProgress: handleProgress,
      });
      onUploadComplete?.(uploaded);
      // Drop the successful entries from the queue after a short
      // green-check beat so the user sees the success state.
      window.setTimeout(() => {
        setQueue((prev) => prev.filter((entry) => entry.status !== "success"));
      }, 1200);
    } catch {
      // Per-file ticks already surfaced individual errors. Nothing else
      // to do — the user clicks "Thử lại" per card.
    }
  };

  const retryEntry = (entry: QueueEntry) => {
    setQueue((prev) =>
      prev.map((e) =>
        e.key === entry.key
          ? { ...e, status: "pending", percent: 0, error: undefined }
          : e,
      ),
    );
    void uploadMutation
      .mutateAsync({
        files: [entry.file],
        onPerFileProgress: handleProgress,
      })
      .then((uploaded) => {
        if (uploaded.length > 0) onUploadComplete?.(uploaded);
        window.setTimeout(() => {
          setQueue((prev) => prev.filter((e) => e.key !== entry.key));
        }, 1200);
      })
      .catch(() => {
        /* per-file tick already updated the entry */
      });
  };

  const existingImages = imagesQuery.data ?? [];
  const isBusy = uploadMutation.isPending;
  const queueSize = queue.length;
  const reachedLimit = queueSize >= maxFiles;

  return (
    <section
      aria-label="Tải ảnh bất động sản"
      className="flex flex-col gap-4"
      data-testid="image-uploader"
    >
      {/* Hidden inputs. Camera-capable input is mobile-only and gets
          tap-routed by clicking the "Chụp / chọn ảnh" button. */}
      <input
        ref={cameraInputRef}
        type="file"
        multiple
        accept={ACCEPT_MIME}
        capture="environment"
        className="sr-only"
        onChange={onPickFiles}
        data-testid="image-uploader-camera-input"
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_MIME}
        className="sr-only"
        onChange={onPickFiles}
        data-testid="image-uploader-file-input"
      />

      {/* Add-photos affordance. On mobile we route the click to the
          camera-capable input; on desktop we use the plain picker. */}
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          size="lg"
          className="h-14 w-full text-base md:hidden"
          disabled={isBusy || reachedLimit}
          onClick={() => cameraInputRef.current?.click()}
        >
          <Camera aria-hidden="true" className="size-5" />
          Chụp / chọn ảnh
        </Button>

        {/* Desktop: same button + drag-zone. */}
        <Button
          type="button"
          size="lg"
          className="hidden h-12 w-full md:inline-flex"
          disabled={isBusy || reachedLimit}
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon aria-hidden="true" className="size-5" />
          Chọn ảnh
        </Button>

        <div
          role="presentation"
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDropFiles}
          className={cn(
            "hidden items-center justify-center rounded-lg border-2 border-dashed p-6 text-sm text-muted-foreground transition-colors md:flex",
            isDragOver ? "border-primary bg-primary/5" : "border-border",
          )}
        >
          Hoặc kéo thả ảnh vào đây
        </div>
        {reachedLimit ? (
          <p className="text-xs text-destructive" role="status">
            Đã đạt giới hạn {maxFiles} ảnh. Hãy tải lên trước khi chọn thêm.
          </p>
        ) : null}
      </div>

      {/* Selection preview row. */}
      {queueSize > 0 ? (
        <div className="flex flex-col gap-2">
          <header className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">Đã chọn {queueSize} ảnh</p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={clearQueue}
                disabled={isBusy}
              >
                Hủy
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void startUpload()}
                disabled={
                  isBusy ||
                  queue.every((e) => e.status === "uploading" || e.status === "success")
                }
              >
                <Upload aria-hidden="true" className="size-4" />
                Tải lên
              </Button>
            </div>
          </header>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {queue.map((entry) => (
              <ImageUploaderCard
                key={entry.key}
                file={entry.file}
                status={entry.status}
                percent={entry.percent}
                error={entry.error}
                onRemove={
                  entry.status === "uploading" ? undefined : () => removeFromQueue(entry.key)
                }
                onRetry={
                  entry.status === "error" ? () => retryEntry(entry) : undefined
                }
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Existing images grid. */}
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Ảnh hiện có{" "}
          {existingImages.length > 0 ? (
            <span className="ml-1 text-foreground">({existingImages.length})</span>
          ) : null}
        </h3>
        {imagesQuery.isPending ? (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] w-full" />
            ))}
          </div>
        ) : existingImages.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/60 bg-muted/20 p-4 text-center text-sm text-muted-foreground">
            Chưa có ảnh. Hãy tải ảnh đầu tiên cho bất động sản này.
          </p>
        ) : (
          <PropertyImageGrid propertyId={propertyId} images={existingImages} />
        )}
      </div>
    </section>
  );
}
